import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { Button } from "~/foundation/components/Button";
import { refineText } from "./commandApi";
import type { PlanDraft } from "./commandTypes";

// The work item the person is about to file, still in their hands: the title, the project, the kind, the brief the
// developer's IDE agent will receive, and what "done" means. The brief has the document bar's AI verbs beside it —
// Elaborate · Shorter · Clearer · your own instruction — each a rewrite by the same small model, with an undo.
// Owner: "it will elaborate the prompt and change it to a more elaborate one and then the user can use the bar like in
// the documents — that has ai features — this is for the prompt of the plan — then it goes to the ide".

const KINDS = ["feature", "task", "bug", "story"] as const;

export function PlanDraftEditor({
  draft,
  projects,
  onChange,
}: {
  draft: PlanDraft;
  projects: Array<{ key: string; name: string }>;
  onChange: (next: PlanDraft) => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [previous, setPrevious] = useState<string | null>(null);
  const [instruction, setInstruction] = useState("");
  const refine = useMutation({
    mutationFn: (how: string) => refineText(api, draft.prompt, how),
    onSuccess: (text) => {
      setPrevious(draft.prompt);
      onChange({ ...draft, prompt: text });
    },
  });
  const field =
    "w-full rounded-sm border border-input-border bg-bg px-2 py-1 text-body text-text outline-none focus:border-input-border-focus";
  const id = draft.planRef;

  return (
    <div className="flex flex-col gap-2" data-testid="plan-draft">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
        <label className="flex flex-col gap-1 text-small text-text-secondary">
          {t("Title")}
          <input
            className={field}
            value={draft.title}
            onChange={(e) => onChange({ ...draft, title: e.target.value })}
            aria-label={t("Title")}
          />
        </label>
        <label className="flex flex-col gap-1 text-small text-text-secondary">
          {t("Project")}
          <select
            className={field}
            value={draft.projectKey ?? ""}
            onChange={(e) => {
              const key = e.target.value || null;
              const name = projects.find((p) => p.key === key)?.name ?? null;
              onChange({ ...draft, projectKey: key, projectName: name });
            }}
            aria-label={t("Project")}
          >
            <option value="">{t("Pick a project…")}</option>
            {projects.map((p) => (
              <option key={p.key} value={p.key}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-small text-text-secondary">
          {t("Kind")}
          <select
            className={field}
            value={draft.kind}
            onChange={(e) => onChange({ ...draft, kind: e.target.value })}
            aria-label={t("Kind")}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label htmlFor={`brief-${id}`} className="text-small text-text-secondary">
        {t("The brief the developer's IDE agent will receive")}
      </label>
      <textarea
        id={`brief-${id}`}
        className={`${field} min-h-[9rem] font-mono text-small`}
        value={draft.prompt}
        onChange={(e) => onChange({ ...draft, prompt: e.target.value })}
        rows={8}
      />
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-small text-text-tertiary">{t("Rewrite with AI:")}</span>
        {(["elaborate", "shorter", "clearer"] as const).map((how) => (
          <Button
            key={how}
            size="sm"
            variant="neutral"
            disabled={refine.isPending || draft.prompt.trim().length === 0}
            onClick={() => refine.mutate(how)}
          >
            {how === "elaborate" ? t("Elaborate") : how === "shorter" ? t("Shorter") : t("Clearer")}
          </Button>
        ))}
        <input
          className={`${field} max-w-[16rem] flex-1`}
          placeholder={t("…or say how to change it")}
          aria-label={t("Your own instruction")}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && instruction.trim()) {
              e.preventDefault();
              refine.mutate(instruction.trim());
            }
          }}
        />
        <Button
          size="sm"
          variant="neutral"
          disabled={refine.isPending || !instruction.trim()}
          onClick={() => refine.mutate(instruction.trim())}
        >
          {refine.isPending ? t("Rewriting…") : t("Rewrite")}
        </Button>
        {previous !== null ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onChange({ ...draft, prompt: previous });
              setPrevious(null);
            }}
          >
            {t("Undo rewrite")}
          </Button>
        ) : null}
      </div>
      {refine.error ? (
        <p role="alert" className="text-danger text-small">
          {refine.error instanceof Error ? refine.error.message : t("The rewrite did not work.")}
        </p>
      ) : null}

      <label htmlFor={`accept-${id}`} className="text-small text-text-secondary">
        {t("Done means (one check per line)")}
      </label>
      <textarea
        id={`accept-${id}`}
        className={`${field} text-small`}
        value={draft.acceptance.join("\n")}
        onChange={(e) => onChange({ ...draft, acceptance: e.target.value.split("\n") })}
        rows={3}
      />
    </div>
  );
}
